/**
 * Chatbot widget. Usage:
 * `Assilya.chatbot.init({ apiUrl, apiKey, locale?, userContext?, bottomOffset?, bottomOffsetMobile? })`.
 *
 * `bottomOffset` / `bottomOffsetMobile` are distances in px from the viewport
 * bottom (desktop / viewports up to 480px wide). They replace the stylesheet
 * defaults (20px / 12px) so the launcher can clear a sticky bar or cookie
 * banner already sitting in the bottom-right corner of the host page.
 */
import styles from './styles.css?inline';
import { fetchConfig } from './config.js';
import { postMessageStream, postPreset } from './sse.js';
import { createStorage } from './storage.js';
import { identityHeaders } from './identity.js';
import { pickCandidate } from './i18n.js';
import { createUi } from './dom.js';
import { renderMarkdown } from './markdown.js';
import { createSpeechRecognizer, isSupported as isVoiceSupported } from './speech.js';

let active = null;

function injectStyles() {
    if (document.getElementById('mdd-chatbot-styles')) return;
    const el = document.createElement('style');
    el.id = 'mdd-chatbot-styles';
    el.textContent = styles;
    document.head.appendChild(el);
}

function normalizeUrl(raw) {
    if (typeof raw !== 'string' || !raw) return '';
    return raw.replace(/\/+$/, '');
}

/**
 * @returns {number|null} non-negative integer px, or null when the option is absent or invalid
 */
function normalizeOffset(raw) {
    if (raw === undefined || raw === null || raw === '') return null;
    const value = Math.round(Number(raw));
    return Number.isFinite(value) && value >= 0 ? value : null;
}

function buildCommonHeaders({ apiKey, locale, sessionToken, userContext }) {
    const headers = {
        Authorization: 'Bearer ' + apiKey,
        'X-Assilya-Thread': sessionToken,
        ...identityHeaders(userContext),
    };

    if (locale) {
        headers['X-Assilya-Locale'] = locale;
    }

    return headers;
}

async function init(options) {
    const apiUrl = normalizeUrl(options && options.apiUrl);
    const apiKey = options && options.apiKey;

    if (!apiUrl) {
        console.error('[Chatbot] init() requires an apiUrl.');
        return createDestroyed();
    }

    if (!apiKey) {
        console.error('[Chatbot] init() requires an apiKey.');
        return createDestroyed();
    }

    if (active) {
        active.destroy();
    }

    injectStyles();

    // i18n.js gives us a guess; the server is authoritative — `config.locale`
    // returned below is the resolved (clamped) locale we'll use everywhere
    // after this point.
    const candidateLocale = pickCandidate(options.locale);
    const storage = createStorage(apiKey);
    let sessionToken = storage.getOrCreateSession();
    const userContext = options.userContext || null;
    const offsets = {
        bottom: normalizeOffset(options.bottomOffset),
        bottomMobile: normalizeOffset(options.bottomOffsetMobile),
    };

    let config;
    try {
        config = await fetchConfig({ apiUrl, apiKey, locale: candidateLocale, userContext });
    } catch (err) {
        console.error('[Chatbot] failed to fetch config', err);
        return createDestroyed();
    }

    // From here on, every chatbot call uses the server-resolved locale.
    const locale = typeof config.locale === 'string' && config.locale ? config.locale : candidateLocale;

    const strings = Object.assign(
        {
            send: 'Send',
            placeholder: 'Type a message...',
            typing: 'Assistant is replying...',
            tool_call_hint: 'Looking that up...',
            reset: 'New conversation',
            reset_confirm: 'Clear the current conversation?',
            close: 'Close',
            open: 'Open chat',
            error_generic: 'An error occurred, please try again.',
            service_unavailable: 'The assistant is temporarily unavailable, please try again later.',
            voice_start: 'Start voice input',
            voice_stop: 'Stop voice input',
            voice_error: 'Voice input failed, please try again.',
        },
        config.strings || {}
    );

    let currentAbort = null;

    const voiceSupported = isVoiceSupported();

    const ui = createUi({
        config,
        strings,
        voiceSupported,
        offsets,
        onSend: sendMessage,
        onPreset: handlePreset,
        onReset: resetConversation,
        onVoiceToggle: toggleVoiceInput,
        onOpenStateChange(isOpen) {
            if (config.widget.persist_open_state) {
                storage.setOpenState(isOpen);
            }
        },
    });

    const recognizer = voiceSupported
        ? createSpeechRecognizer({
              locale,
              onStart() {
                  ui.setMicListening(true);
                  ui.setInputValue('');
              },
              onInterim(text) {
                  ui.setInputValue(text);
              },
              onEnd() {
                  ui.setMicListening(false);
              },
              onFinal(text) {
                  ui.setInputValue('');
                  sendMessage(text);
              },
              onError(code) {
                  ui.setMicListening(false);
                  if (code === 'no-speech' || code === 'aborted') return;
                  ui.addErrorBubble(strings.voice_error);
              },
          })
        : null;

    function toggleVoiceInput() {
        if (!recognizer) return;
        if (recognizer.isActive()) {
            recognizer.stop();
        } else {
            recognizer.start();
        }
    }

    const savedMessages = storage.getMessages();
    if (savedMessages.length > 0) {
        ui.clearMessages();
        for (const message of savedMessages) {
            if (message.role === 'user') {
                ui.addUserBubble(message.content);
            } else {
                const bubble = ui.addBotBubble();
                bubble.innerHTML = renderMarkdown(message.content);
            }
        }
        if (storage.presetsUsed()) {
            ui.hidePresets();
        }
    }

    if (config.widget.persist_open_state && storage.getOpenState()) {
        ui.setOpen(true);
    }

    function sendMessage(text) {
        const userEl = ui.addUserBubble(text);
        void userEl;
        storage.appendMessage('user', text);
        let typingEl = ui.addTypingIndicator();
        const botEl = ui.addBotBubble();
        let accumulated = '';
        let toolHint = null;

        function paintStreamingMarkdown(markdown) {
            botEl.innerHTML = renderMarkdown(markdown);
            const target = botEl.lastElementChild || botEl;
            const caret = document.createElement('span');
            caret.className = 'mdd-chatbot__caret';
            caret.setAttribute('aria-hidden', 'true');
            target.appendChild(caret);
        }

        ui.setBusy(true);

        if (currentAbort) currentAbort.abort();
        currentAbort = new AbortController();

        postMessageStream({
            url: apiUrl + config.endpoints.message,
            body: { message: text },
            headers: buildCommonHeaders({ apiKey, locale, sessionToken, userContext }),
            signal: currentAbort.signal,
            onEvent(event) {
                const type = event && event.type;

                if (type === 'text_delta' || type === 'text-delta') {
                    if (typingEl && typingEl.parentNode) {
                        typingEl.parentNode.removeChild(typingEl);
                        typingEl = null;
                    }
                    if (toolHint && toolHint.parentNode) {
                        toolHint.parentNode.removeChild(toolHint);
                    }
                    toolHint = null;
                    accumulated += event.delta || event.text || '';
                    paintStreamingMarkdown(accumulated);
                } else if (type === 'tool_call' || type === 'tool-call') {
                    if (!toolHint) {
                        toolHint = document.createElement('div');
                        toolHint.className = 'mdd-chatbot__tool-hint';
                        toolHint.textContent = strings.tool_call_hint;
                        botEl.after(toolHint);
                    }
                } else if (type === 'error') {
                    if (typingEl && typingEl.parentNode) typingEl.parentNode.removeChild(typingEl);
                    if (botEl.parentNode) botEl.parentNode.removeChild(botEl);
                    // `reason: 'service_unavailable'` = the AI provider is down;
                    // `reason: 'plan_limit'` = the workspace subscription does not
                    // allow a new conversation. Both are "come back later" for the
                    // visitor — the subscription is not their problem to see.
                    ui.addErrorBubble(event.reason === 'service_unavailable' || event.reason === 'plan_limit'
                        ? strings.service_unavailable
                        : strings.error_generic);
                }
            },
            onDone() {
                if (typingEl && typingEl.parentNode) typingEl.parentNode.removeChild(typingEl);
                if (toolHint && toolHint.parentNode) toolHint.parentNode.removeChild(toolHint);
                if (accumulated) {
                    botEl.innerHTML = renderMarkdown(accumulated);
                    storage.appendMessage('assistant', accumulated);
                } else if (botEl.parentNode) {
                    botEl.parentNode.removeChild(botEl);
                }
                ui.setBusy(false);
                ui.focusInput();
                currentAbort = null;
            },
            onError(err) {
                console.error('[Chatbot] stream error', err);
                if (typingEl && typingEl.parentNode) typingEl.parentNode.removeChild(typingEl);
                if (toolHint && toolHint.parentNode) toolHint.parentNode.removeChild(toolHint);
                if (botEl.parentNode) botEl.parentNode.removeChild(botEl);
                ui.addErrorBubble(strings.error_generic);
                ui.setBusy(false);
                ui.focusInput();
                currentAbort = null;
            },
        });
    }

    function handlePreset({ id, hasAuto, display }) {
        storage.markPresetsUsed();
        ui.hidePresets();

        if (!hasAuto) {
            sendMessage(display);
            return;
        }

        ui.addUserBubble(display);
        storage.appendMessage('user', display);
        const typingEl = ui.addTypingIndicator();

        postPreset({
            url: apiUrl + config.endpoints.preset,
            body: { preset_id: id },
            headers: buildCommonHeaders({ apiKey, locale, sessionToken, userContext }),
        })
            .then((resp) => {
                if (typingEl && typingEl.parentNode) typingEl.parentNode.removeChild(typingEl);
                const bot = ui.addBotBubble();
                const assistantText = resp.assistant_message || '';
                bot.innerHTML = renderMarkdown(assistantText);
                storage.appendMessage('assistant', assistantText);
                ui.focusInput();
            })
            .catch((err) => {
                console.error('[Chatbot] preset error', err);
                if (typingEl && typingEl.parentNode) typingEl.parentNode.removeChild(typingEl);
                ui.addErrorBubble(strings.error_generic);
                ui.focusInput();
            });
    }

    function resetConversation() {
        if (currentAbort) currentAbort.abort();
        currentAbort = null;
        if (recognizer) recognizer.abort();
        storage.clearMessages();
        sessionToken = storage.resetSession();
        ui.clearMessages();
        ui.renderIntro();
        ui.showPresets();
    }

    const api = {
        destroy() {
            if (currentAbort) currentAbort.abort();
            if (recognizer) recognizer.abort();
            if (ui && ui.root && ui.root.parentNode) {
                ui.root.parentNode.removeChild(ui.root);
            }
            active = null;
        },
        toggle(open) {
            ui.setOpen(typeof open === 'boolean' ? open : !ui.isOpen());
        },
        reset() {
            resetConversation();
        },
    };

    active = api;
    return api;
}

function createDestroyed() {
    return {
        destroy() {},
        toggle() {},
        reset() {},
    };
}

if (typeof window !== 'undefined') {
    window.Assilya = window.Assilya || {};
    window.Assilya.chatbot = { init };
}

export default { init };
